import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { Container, Card, Button, ListGroup, Row, Col, Form } from 'react-bootstrap';
import NavbarGlobal from './NavbarGlobal';
import { db as appDb } from '../firebase/firebase';
import { AppContext } from '../Context';

const RegistroDePracticasDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useContext(AppContext);
  const db = appDb;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const nivel = params.get('nivel') || '';
  const modulo = params.get('modulo') || '';
  const nombre = params.get('nombre') || '';

  const [items, setItems] = useState([]);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [openIds, setOpenIds] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const qBase = query(collection(db, 'evaluaciones'), where('userId', '==', id));
        const snap = await getDocs(qBase);
        const datos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const filtrados = datos
          .filter(e => !nivel || e.nivel === nivel)
          .filter(e => !modulo || e.modulo === modulo)
          .filter(e => !nombre || e.nombre === nombre);
        filtrados.sort((a, b) => {
          const da = a?.createdAt?.toDate ? a.createdAt.toDate() : new Date(a?.createdAt);
          const db_ = b?.createdAt?.toDate ? b.createdAt.toDate() : new Date(b?.createdAt);
          return (db_?.getTime?.() || 0) - (da?.getTime?.() || 0);
        });
        setItems(filtrados);
      } catch (e) {
        console.error(e);
      }
    };
    if (currentUser && id) load();
  }, [id, currentUser, db, nivel, modulo, nombre]);

  const enRango = (ev) => {
    const d = ev?.createdAt?.toDate ? ev.createdAt.toDate() : new Date(ev?.createdAt);
    if (fechaDesde) {
      const from = new Date(fechaDesde);
      if (d < new Date(from.getFullYear(), from.getMonth(), from.getDate())) return false;
    }
    if (fechaHasta) {
      const to = new Date(fechaHasta);
      const end = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
      if (d > end) return false;
    }
    return true;
  };

  const itemsFiltrados = items.filter(enRango);

  const formatFecha = (ev) => {
    try {
      const d = ev?.createdAt?.toDate ? ev.createdAt.toDate() : new Date(ev?.createdAt);
      return isNaN(d) ? '' : d.toLocaleString();
    } catch { return ''; }
  };

  const isImgUrl = (val) =>
    typeof val === 'string' &&
    (/\.(png|jpe?g|gif|webp|svg)$/i.test(val) || val.includes('firebasestorage.googleapis.com'));

  const toggleOpen = (id) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderRespuestasTable = (ev) => {
    const filas = [];
    const pushPar = (label, val, key) => (
      <tr key={key}>
        <td className="text-muted" style={{ width: '35%' }}>{label}</td>
        <td>
          {isImgUrl(val) ? (
            <Button as="a" href={val} target="_blank" rel="noopener noreferrer" size="sm" variant="outline-secondary">Ver</Button>
          ) : Array.isArray(val) ? (
            val.join(', ')
          ) : typeof val === 'object' && val !== null ? (
            Object.entries(val).map(([kk, vv], i) => (
              <div key={`${key}-${i}`}>{`${kk}: ${Array.isArray(vv) ? vv.join(', ') : String(vv)}`}</div>
            ))
          ) : (
            String(val)
          )}
        </td>
      </tr>
    );
    if (ev?.respuestas && typeof ev.respuestas === 'object' && !Array.isArray(ev.respuestas)) {
      Object.entries(ev.respuestas).forEach(([k, v], idx) => filas.push(pushPar(k, v, `resp-obj-${idx}`)));
    } else if (Array.isArray(ev?.respuestas)) {
      ev.respuestas.forEach((v, idx) => filas.push(pushPar(`Respuesta ${idx + 1}`, v, `resp-arr-${idx}`)));
    } else if (typeof ev?.respuesta !== 'undefined') {
      filas.push(pushPar('Respuesta', ev.respuesta, 'respuesta-unica'));
    }
    const omit = new Set(['id','userId','nombre','nivel','modulo','tipo','createdAt','pregunta','respuestas','respuesta']);
    Object.entries(ev)
      .filter(([k, v]) => !omit.has(k) && v != null)
      .forEach(([k, v], idx) => filas.push(pushPar(k, v, `extra-${idx}`)));
    if (!filas.length) {
      filas.push(
        <tr key="sin-resp">
          <td className="text-muted">Respuestas</td>
          <td>No hay respuestas</td>
        </tr>
      );
    }
    return (
      <div className="border rounded p-2">
        <table className="table table-sm table-bordered mb-0">
          <tbody>{filas}</tbody>
        </table>
      </div>
    );
  };

  const titulo = useMemo(() => {
    const idxSep = (nombre || '').indexOf(':');
    const t = idxSep >= 0 ? nombre.slice(idxSep + 1).trim() : '';
    return t;
  }, [nombre]);

  return (
    <Container>
      <NavbarGlobal
        title="Resultados por Ejercicio"
        showBackButton={true}
      />
      <Card className="mb-3">
        <Card.Body>
          <h5 className="mb-1">{`Nivel ${nivel || '—'}`}</h5>
          <div className="text-muted">{`Módulo ${modulo || '—'} - ${nombre || 'Ejercicio'}`}</div>
          {titulo && <div className="small text-muted">{titulo}</div>}
        </Card.Body>
      </Card>

      <Form className="mb-3">
        <Row className="g-3">
          <Col xs="12" sm="6">
            <Form.Label>Desde</Form.Label>
            <Form.Control type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} />
          </Col>
          <Col xs="12" sm="6">
            <Form.Label>Hasta</Form.Label>
            <Form.Control type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} />
          </Col>
        </Row>
      </Form>

      <Card>
        <ListGroup variant="flush">
          {itemsFiltrados.map((ev) => (
            <ListGroup.Item key={ev.id}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-semibold">{formatFecha(ev)}</div>
                  <div className="small text-muted">
                    {ev?.tipo ? `Tipo: ${ev.tipo}` : ''}
                    {ev?.pregunta ? ` • Pregunta: ${ev.pregunta}` : ''}
                  </div>
                </div>
                <div className="ms-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => toggleOpen(ev.id)}
                  >
                    Ver respuestas
                  </Button>
                </div>
              </div>
              <div className={`mt-2 ${openIds.has(ev.id) ? '' : 'd-none'}`}>
                {renderRespuestasTable(ev)}
                <div className="mt-2">
                  <Button
                    as="a"
                    href={`/cuaderno-digital?nivel=${nivel}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    variant="outline-secondary"
                  >
                    Ir al ejercicio
                  </Button>
                </div>
              </div>
            </ListGroup.Item>
          ))}
          {itemsFiltrados.length === 0 && (
            <ListGroup.Item>No hay intentos registrados con los filtros aplicados.</ListGroup.Item>
          )}
        </ListGroup>
      </Card>
    </Container>
  );
};

export default RegistroDePracticasDetalle;

